import { useEffect } from 'react'

import { useAppSelector } from 'reduxTyped'
import { getCurrentAccountID } from 'common/core/selectors/appSelectors'
import { accountUserApi } from 'shared/api/requests/accountUser'

import { getJwtExpiresAt, getRefreshDelayMs, toIntercomSettings } from './intercomFinAISettings'

export const RETRY_DELAYS_MS = [30_000, 60_000, 120_000]

const getCurrentExpiresAt = () => {
  const settings = window.intercomFinAISettings
  return settings?.expires_at ?? getJwtExpiresAt(settings?.intercom_user_jwt)
}

export const useIntercomFinAISettingsRefresh = ({
  isActive,
  onDisabled,
}: {
  isActive: boolean
  onDisabled: () => void
}) => {
  const accountId = useAppSelector(getCurrentAccountID)

  useEffect(() => {
    if (!isActive || !accountId) {
      return
    }

    let isCancelled = false
    let isRefreshing = false
    let failedAttempts = 0
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let expiresAt = getCurrentExpiresAt()

    const schedule = (delayMs: number) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => void refresh(), delayMs)
    }

    const scheduleFromExpiry = () => {
      schedule(expiresAt === null ? 0 : getRefreshDelayMs(expiresAt))
    }

    const refresh = async () => {
      if (isRefreshing) {
        return
      }
      isRefreshing = true
      clearTimeout(timeoutId)

      try {
        const { data } = await accountUserApi.intercomFinAISettings()
        if (isCancelled) {
          return
        }
        failedAttempts = 0

        if (!data.settings) {
          delete window.intercomFinAISettings
          onDisabled()
          return
        }

        window.intercomFinAISettings = data.settings
        window.Intercom?.('update', toIntercomSettings(data.settings))

        expiresAt = data.settings.expires_at
        scheduleFromExpiry()
      } catch {
        if (isCancelled) {
          return
        }
        schedule(RETRY_DELAYS_MS[Math.min(failedAttempts, RETRY_DELAYS_MS.length - 1)])
        failedAttempts += 1
      } finally {
        isRefreshing = false
      }
    }

    const refreshIfDue = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      if (expiresAt === null || getRefreshDelayMs(expiresAt) === 0) {
        void refresh()
      }
    }

    scheduleFromExpiry()
    document.addEventListener('visibilitychange', refreshIfDue)
    window.addEventListener('online', refreshIfDue)

    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', refreshIfDue)
      window.removeEventListener('online', refreshIfDue)
    }
  }, [isActive, accountId, onDisabled])
}
